<script lang="ts">
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import ScrollReveal from '$lib/components/ScrollReveal.svelte';
	import GlowCard from '$lib/components/GlowCard.svelte';
	import MagneticButton from '$lib/components/MagneticButton.svelte';
	import { t, type Language } from '$lib/i18n';
	import { captureEvent } from '$lib/analytics/posthog';

	let { data } = $props();
	let lang = $derived($page.params.lang as Language);
	let tr = $derived(t(lang));
	let serviceId = $derived(data.serviceId);

	onMount(() => {
		captureEvent('service_detail_viewed', { service: serviceId, lang });
	});

	const serviceIcons: Record<string, string> = {
		integration:
			'<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
		iot: '<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/></svg>',
		maintenance:
			'<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>',
		dashboards:
			'<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>',
		custom:
			'<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>',
		ai: '<svg aria-hidden="true" class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>'
	};

	const relatedServices: Record<string, string[]> = {
		integration: ['iot', 'dashboards', 'custom'],
		iot: ['integration', 'maintenance', 'dashboards'],
		maintenance: ['iot', 'ai', 'dashboards'],
		dashboards: ['integration', 'maintenance', 'custom'],
		custom: ['integration', 'dashboards', 'ai'],
		ai: ['maintenance', 'iot', 'custom']
	};

	let titleKey = $derived(`services_${serviceId}_title`);
	let taglineKey = $derived(`services_${serviceId}_tagline`);
	let descKey = $derived(`services_${serviceId}_desc`);
	let extendedDescKey = $derived(`services_detail_${serviceId}_extended_desc`);
	let challengeKeys = $derived([1, 2, 3, 4].map((i) => `services_${serviceId}_challenge_${i}`));
	let approachKeys = $derived([1, 2, 3, 4].map((i) => `services_${serviceId}_approach_${i}`));
	let usecaseKeys = $derived([1, 2, 3].map((i) => `services_detail_${serviceId}_usecase_${i}`));
	let related = $derived(relatedServices[serviceId] || []);
</script>

<svelte:head>
	<title>{tr(titleKey)} | Ameta</title>
	<meta name="description" content={tr(descKey)} />
</svelte:head>

<!-- Hero -->
<section class="relative pt-32 pb-20 overflow-hidden">
	<div
		class="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-transparent"
	></div>
	<div class="absolute inset-0 bg-grid opacity-20"></div>

	<!-- Animated hero background per service -->
	<div class="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
		{#if serviceId === 'iot'}
			<!-- Floating dots for IoT -->
			{#each Array(12) as __, i}
				<div
					class="absolute w-2 h-2 rounded-full bg-ameta-green/20 animate-float"
					style="left: {8 + i * 8}%; top: {20 + (i % 3) * 25}%; animation-delay: {i *
						0.3}s; animation-duration: {3 + (i % 3)}s"
				></div>
			{/each}
		{:else if serviceId === 'integration'}
			<!-- Horizontal data streams -->
			{#each Array(5) as __, i}
				<div
					class="absolute h-px bg-gradient-to-r from-transparent via-ameta-green/30 to-transparent animate-slide-right"
					style="top: {25 + i * 12}%; width: 40%; animation-delay: {i *
						0.5}s; animation-duration: {4 + i}s"
				></div>
			{/each}
		{:else if serviceId === 'dashboards'}
			<!-- Rising bars -->
			{#each Array(8) as __, i}
				<div
					class="absolute bottom-0 bg-ameta-green/5 rounded-t animate-grow-up"
					style="left: {5 + i * 12}%; width: 3%; height: {20 +
						((i * 7) % 40)}%; animation-delay: {i * 0.2}s"
				></div>
			{/each}
		{:else if serviceId === 'ai'}
			<!-- Pulse rings -->
			{#each Array(3) as __, i}
				<div
					class="absolute rounded-full border border-ameta-accent/10 animate-ping-slow"
					style="left: 50%; top: 40%; width: {200 + i * 120}px; height: {200 +
						i * 120}px; transform: translate(-50%, -50%); animation-delay: {i * 1}s"
				></div>
			{/each}
		{:else if serviceId === 'maintenance'}
			<!-- Lightning pulses -->
			{#each Array(4) as __, i}
				<div
					class="absolute w-1 bg-gradient-to-b from-ameta-green/20 to-transparent animate-flash"
					style="left: {20 + i * 20}%; top: 10%; height: 60%; animation-delay: {i *
						0.8}s; animation-duration: {2 + i * 0.5}s"
				></div>
			{/each}
		{:else}
			<!-- Default: subtle grid pulse -->
			<div class="absolute inset-0 bg-ameta-green/[0.02] animate-pulse"></div>
		{/if}
	</div>

	<div class="relative max-w-7xl mx-auto px-6">
		<!-- Breadcrumbs -->
		<nav class="flex items-center gap-2 text-sm text-slate-500 mb-8" aria-label="Breadcrumb">
			<a href="/{lang}" class="hover:text-ameta-green transition-colors">{tr('nav_home')}</a>
			<svg aria-hidden="true" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"
				><path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M9 5l7 7-7 7"
				/></svg
			>
			<a href="/{lang}/services" class="hover:text-ameta-green transition-colors"
				>{tr('nav_services')}</a
			>
			<svg aria-hidden="true" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"
				><path
					stroke-linecap="round"
					stroke-linejoin="round"
					stroke-width="2"
					d="M9 5l7 7-7 7"
				/></svg
			>
			<span class="text-slate-300">{tr(titleKey)}</span>
		</nav>

		<ScrollReveal>
			<div class="flex items-start gap-6 mb-6">
				<div
					class="w-20 h-20 rounded-2xl bg-ameta-green/10 flex items-center justify-center text-ameta-green flex-shrink-0"
				>
					{@html serviceIcons[serviceId] || ''}
				</div>
				<div>
					<h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-3">{tr(titleKey)}</h1>
					<p class="text-xl md:text-2xl text-ameta-green">{tr(taglineKey)}</p>
				</div>
			</div>

			<p class="text-lg text-slate-400 leading-relaxed max-w-3xl mt-8">{tr(descKey)}</p>
			<p class="text-lg text-slate-400 leading-relaxed max-w-3xl mt-4">{tr(extendedDescKey)}</p>
		</ScrollReveal>
	</div>
</section>

<!-- Challenges & Approach -->
<section class="py-16">
	<div class="max-w-7xl mx-auto px-6">
		<div class="grid lg:grid-cols-2 gap-8">
			<ScrollReveal>
				<GlowCard>
					<div class="p-8">
						<h2 class="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-6">
							{tr('services_page_challenges')}
						</h2>
						<ul class="space-y-4">
							{#each challengeKeys as key}
								<li class="flex items-start gap-3">
									<svg
										aria-hidden="true"
										class="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
										/>
									</svg>
									<span class="text-slate-400">{tr(key)}</span>
								</li>
							{/each}
						</ul>
					</div>
				</GlowCard>
			</ScrollReveal>

			<ScrollReveal delay={100}>
				<GlowCard>
					<div class="p-8">
						<h2 class="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-6">
							{tr('services_page_approach')}
						</h2>
						<ol class="space-y-4">
							{#each approachKeys as key, j}
								<li class="flex items-start gap-3">
									<span
										class="w-7 h-7 rounded-full bg-ameta-green/20 text-ameta-green text-sm font-bold flex items-center justify-center flex-shrink-0"
									>
										{j + 1}
									</span>
									<span class="text-slate-400">{tr(key)}</span>
								</li>
							{/each}
						</ol>
					</div>
				</GlowCard>
			</ScrollReveal>
		</div>
	</div>
</section>

<!-- Use Cases -->
<section class="py-16">
	<div class="max-w-7xl mx-auto px-6">
		<h2 class="text-2xl font-bold mb-8">{tr('services_detail_usecases_title')}</h2>
		<div class="grid md:grid-cols-3 gap-6">
			{#each usecaseKeys as key, i}
				<ScrollReveal delay={i * 150}>
					<GlowCard>
						<div class="p-6">
							<div
								class="w-10 h-10 rounded-lg bg-ameta-green/10 text-ameta-green flex items-center justify-center mb-4 font-bold text-lg"
							>
								{i + 1}
							</div>
							<p class="text-slate-300">{tr(key)}</p>
						</div>
					</GlowCard>
				</ScrollReveal>
			{/each}
		</div>
	</div>
</section>

<!-- Related Services -->
<section class="py-16">
	<div class="max-w-7xl mx-auto px-6">
		<ScrollReveal>
			<h2 class="text-2xl font-bold mb-8">{tr('services_detail_related_title')}</h2>
			<div class="grid md:grid-cols-3 gap-6">
				{#each related as relId, i}
					<ScrollReveal delay={i * 100}>
						<GlowCard>
							<a href="/{lang}/services/{relId}" class="p-6 block group">
								<div
									class="w-12 h-12 rounded-xl bg-ameta-green/10 flex items-center justify-center text-ameta-green mb-4"
								>
									{@html serviceIcons[relId] || ''}
								</div>
								<h3
									class="text-lg font-semibold mb-2 group-hover:text-ameta-green transition-colors"
								>
									{tr(`services_${relId}_title`)}
								</h3>
								<p class="text-sm text-slate-400 line-clamp-2">{tr(`services_${relId}_tagline`)}</p>
							</a>
						</GlowCard>
					</ScrollReveal>
				{/each}
			</div>
		</ScrollReveal>
	</div>
</section>

<!-- CTA -->
<section class="py-24 relative section-gradient">
	<div class="max-w-4xl mx-auto px-6 text-center">
		<ScrollReveal>
			<h2 class="text-3xl md:text-4xl font-bold mb-6">{tr('services_detail_cta_title')}</h2>
			<p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">{tr('services_detail_cta_desc')}</p>
			<MagneticButton
				href="/{lang}/#contact"
				class="btn-primary text-lg inline-flex items-center gap-3 glow-green"
			>
				<span>{tr('services_detail_cta_button')}</span>
				<svg
					aria-hidden="true"
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M17 8l4 4m0 0l-4 4m4-4H3"
					/>
				</svg>
			</MagneticButton>
		</ScrollReveal>
	</div>
</section>
